import ActionTypes from '../../constants/actionTypes';
import { getUser, saveToken, clearToken } from '../../utils/auth';
import { parseExplicitCanSeePermission } from '../../utils/canSeePermission';
import { trackLoginSucceeded, trackDealerLoginAttempted } from './event-tracking.actions';
import { getBuyerId } from './user.actions';
import { clearMyPurchasesFilters } from './ui.actions';

import axios from 'axios';
import AppConstants from '../../constants/appConstants';

export function toggleLoginModal(toggle) {
  return {
    type: ActionTypes.Modal.Login.Toggle,
    showModal: toggle,
  };
}

/**
 * Mapeia o erro da API para o enum login_result
 * @param {Object} error - Objeto de erro do axios
 * @returns {Object} - { loginResult: string, mongoUserId: string|null }
 * 
 * Estrutura de resposta esperada do backend:
 * - 404: { meta: { status: 404, message: "Email não encontrado", mongo_user_id: null } }
 * - 403: { meta: { status: 403, message: "Usuário bloqueado", mongo_user_id: "507f..." } }
 * - 401: { meta: { status: 401, message: "Senha incorreta", mongo_user_id: "507f..." } }
 */
function mapAuthenticationError(error) {
  // Estrutura padrão de retorno
  const result = {
    loginResult: 'invalid_email',
    mongoUserId: null,
  };

  try {
    const errorData = error?.response?.data;
    const statusCode = error?.response?.status;
    const errorMessage = errorData?.meta?.message || '';
    
    // Extrai mongo_user_id do campo meta (padrão definido pelo backend)
    const mongoUserId = errorData?.meta?.mongo_user_id || null;

    // Mapeamento baseado no código de status HTTP
    switch (statusCode) {
      case 404:
        // Email não encontrado
        result.loginResult = 'invalid_email';
        result.mongoUserId = null;
        break;

      case 403:
        // Usuário bloqueado
        result.loginResult = 'blocked_user';
        result.mongoUserId = mongoUserId;
        break;

      case 401:
        // Senha incorreta
        result.loginResult = 'invalid_password';
        result.mongoUserId = mongoUserId;
        break;

      default:
        // Fallback: tenta deduzir pela mensagem de erro
        if (errorMessage.toLowerCase().includes('não encontrado') ||
            errorMessage.toLowerCase().includes('not found')) {
          result.loginResult = 'invalid_email';
          result.mongoUserId = null;
        } else if (errorMessage.toLowerCase().includes('bloqueado') ||
                   errorMessage.toLowerCase().includes('blocked')) {
          result.loginResult = 'blocked_user';
          result.mongoUserId = mongoUserId;
        } else if (errorMessage.toLowerCase().includes('senha') ||
                   errorMessage.toLowerCase().includes('password')) {
          result.loginResult = 'invalid_password';
          result.mongoUserId = mongoUserId;
        }
        break;
    }

    console.log('🔍 Login attempt mapped:', {
      statusCode,
      errorMessage,
      loginResult: result.loginResult,
      mongoUserId: result.mongoUserId,
    });

  } catch (e) {
    // Em caso de erro no parsing, retorna default
    console.error('❌ Erro ao mapear authentication error:', e);
  }

  return result;
}

export const authenticate = ({ username, password }) => async dispatch => {
  dispatch(authenticateStart());
  try {
    const { data } = await axios.post(AppConstants.API.newAuthentication, {
      grant_type: 'password',
      identifier: username,
      password: password,
    });
    const user = getUser(() => data.body);
    
    // Log de sucesso ANTES de qualquer dispatch/redirect
    console.log('✅ Login successful:', {
      email: username,
      userId: user.id,
      dealershipName: user.dealershipName,
      role: user.role,
    });

    // Rastreamento de tentativa de login (Segment) - ANTES do dispatch
    trackDealerLoginAttempted(username, 'success', user.id || null);

    const successAction = authenticateSuccess(data.body, user);
    dispatch(successAction);
    
    // Rastreamento de login bem-sucedido (Mixpanel - legado)
    trackLoginSucceeded(user);

    if (user.dealershipName && user.isLoja()) {
      dispatch(getBuyerId(user.dealershipName));
    } else {
      dispatch({ type: ActionTypes.RelatedDealers.ClearBuyerId });
    }

    return successAction;
  } catch (error) {
    // Mapeia o erro para determinar o tipo de falha
    const { loginResult, mongoUserId } = mapAuthenticationError(error);
    
    // Rastreamento de tentativa de login falhada (Segment)
    trackDealerLoginAttempted(username, loginResult, mongoUserId);

    console.error('❌ Login failed:', {
      email: username,
      loginResult,
      mongoUserId,
      statusCode: error?.response?.status,
      errorMessage: error?.response?.data?.meta?.message,
    });

    const errorMessage = error?.response?.data?.meta?.message || 'Erro ao autenticar';
    const errorAction = authenticateError(errorMessage);
    dispatch(errorAction);
    return errorAction;
  }
};

export function deauthenticate(cb) {
  clearToken();
  return dispatch => {
    dispatch({
      type: ActionTypes.Authenticate.DeAuthenticate,
      user: getUser(() => null),
      token: null,
    });
    dispatch({
      type: ActionTypes.RelatedDealers.Clear,
    });
    dispatch({
      type: ActionTypes.Auction.ClearAuctions,
    });
    dispatch({
      type: ActionTypes.RelatedDealers.ClearBuyerId,
    });
    dispatch(clearMyPurchasesFilters());
    cb();
  };
}

function authenticateSuccess(token, user) {
  saveToken(token);
  return {
    type: ActionTypes.Authenticate.Success,
    user,
    token,
  };
}

function authenticateError(error) {
  return {
    type: ActionTypes.Authenticate.Error,
    error,
  };
}

function authenticateStart() {
  return {
    type: ActionTypes.Authenticate.Perform,
  };
}

export function updateUserPriceVisibility(profile) {
  if (!profile) {
    return { type: ActionTypes.Authenticate.UpdatePriceVisibility, priceVisibility: {} };
  }

  const priceVisibility = {};

  const fipeValues = [
    profile?.canSeeFipePrice,
    profile?.can_see_fipe_price,
    profile?.profile?.canSeeFipePrice,
    profile?.profile?.can_see_fipe_price,
  ];
  const exclusivityValues = [
    profile?.canSeeNegotiateExclusively,
    profile?.can_see_negotiate_exclusively,
    profile?.profile?.canSeeNegotiateExclusively,
    profile?.profile?.can_see_negotiate_exclusively,
  ];
  const buyForValues = [
    profile?.canSeeGuaranteedPurchase,
    profile?.can_see_guaranteed_purchase,
    profile?.profile?.canSeeGuaranteedPurchase,
    profile?.profile?.can_see_guaranteed_purchase,
  ];

  if (fipeValues.some(value => value !== undefined && value !== null)) {
    priceVisibility.canSeeFipePrice = parseExplicitCanSeePermission(...fipeValues);
  }

  if (exclusivityValues.some(value => value !== undefined && value !== null)) {
    priceVisibility.canSeeNegotiateExclusively = parseExplicitCanSeePermission(
      ...exclusivityValues
    );
  }

  if (buyForValues.some(value => value !== undefined && value !== null)) {
    priceVisibility.canSeeGuaranteedPurchase = parseExplicitCanSeePermission(...buyForValues);
  }

  return {
    type: ActionTypes.Authenticate.UpdatePriceVisibility,
    priceVisibility,
  };
}
